// Prueba de audio y conexion antes de llamar (video aprobado "Audio listo antes de llamar").
// Vive dentro de los Ajustes de la isla: revisa navegador, microfono (nivel real), altavoces (tono por la salida
// elegida) y la conexion con la central. Lo que se elige aqui queda en la configuracion del telefono.
// Al cerrar (o al encoger la isla) se apaga el microfono.
import { useEffect, useReducer, useRef, useState, type ReactNode, type RefObject } from 'react';

import { usePhone } from '../hooks';
import {
  barrasEncendidas,
  calificarLatencia,
  cuentaHechos,
  dispositivoValido,
  ensayoInicial,
  estaListo,
  medirCentral,
  mensajeErrorMedios,
  nivelDeMuestras,
  PASOS,
  pasoEnCurso,
  reducirEnsayo,
  revisarNavegador,
  siguientePaso,
  suavizarNivel,
  UMBRAL_VOZ,
  wavTono,
  type AccionEnsayo,
  type EstadoPaso,
  type Fila,
  type PasoId,
} from '../lib/ensayo';
import { Icon } from '../lib/icons';
import { reducedMotion } from '../lib/spring';
import { phone } from './store';

type ConSalida = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
const salidaElegible = () => typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
const BARRAS = 28;

function entorno() {
  const md = navigator.mediaDevices;
  return revisarNavegador({
    seguro: window.isSecureContext,
    rtc: typeof RTCPeerConnection === 'function',
    microfono: !!md && typeof md.getUserMedia === 'function',
    audio: typeof AudioContext === 'function' || 'webkitAudioContext' in window,
  });
}

// Nombre corto de un dispositivo para el resumen ("Auriculares Jabra (Bluetooth)" -> "Auriculares Jabra").
const corto = (d: MediaDeviceInfo | undefined, porDefecto: string) => (d?.label ? d.label.replace(/\s*\([^)]*\)\s*$/, '').replace(/^Predeterminado - |^Default - /i, '') : porDefecto);

export function Ensayo({ onCerrar, onConectar, textoConectar }: { onCerrar: () => void; onConectar: () => void; textoConectar: string }) {
  const s = usePhone();
  const [st, dispatch] = useReducer(reducirEnsayo, undefined, ensayoInicial);
  const [entradas, setEntradas] = useState<MediaDeviceInfo[]>([]);
  const [salidas, setSalidas] = useState<MediaDeviceInfo[]>([]);
  const analizador = useRef<AnalyserNode | null>(null);
  const [micAbierto, setMicAbierto] = useState(false);
  const modo = s.config.modo;
  const micId = s.config.microfono;
  const altId = s.config.altavoz;
  const enCurso = pasoEnCurso(st);
  // Ocupado tambien en la pausa entre un paso y el siguiente (para que el titulo no parpadee).
  const ocupado = !!enCurso || !!siguientePaso(st);
  const listo = estaListo(st);
  const hechos = cuentaHechos(st);

  // Encoger la isla cierra la prueba (y con ello el microfono).
  useEffect(() => {
    if (s.isla === 'pill') onCerrar();
  }, [s.isla, onCerrar]);

  // Lista de microfonos y altavoces (los nombres aparecen despues de dar permiso al microfono).
  const listar = () => {
    const md = navigator.mediaDevices;
    if (!md?.enumerateDevices) return;
    void md
      .enumerateDevices()
      .then((ds) => {
        setEntradas(ds.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications'));
        setSalidas(ds.filter((d) => d.kind === 'audiooutput' && d.deviceId !== 'communications'));
      })
      .catch(() => {});
  };
  useEffect(() => {
    listar();
    const md = navigator.mediaDevices;
    md?.addEventListener?.('devicechange', listar);
    return () => md?.removeEventListener?.('devicechange', listar);
  }, []);

  // Turno del siguiente paso (con una pausa corta para que se vea el avance).
  useEffect(() => {
    const p = siguientePaso(st);
    if (!p) return;
    const t = setTimeout(() => dispatch({ tipo: 'inicia', paso: p }), p === 'nav' ? 450 : 300);
    return () => clearTimeout(t);
  }, [st]);

  // Pasos automaticos: navegador y conexion. Microfono y altavoces esperan a la persona o al nivel de voz.
  useEffect(() => {
    if (enCurso === 'nav') {
      const r = entorno();
      const t = setTimeout(() => dispatch(r.ok ? { tipo: 'ok', paso: 'nav' } : { tipo: 'falla', paso: 'nav', detalle: r.faltan.join('. ') + '.' }), 500);
      return () => clearTimeout(t);
    }
    if (enCurso !== 'con') return;
    if (modo === 'demo') {
      const t = setTimeout(() => dispatch({ tipo: 'ok', paso: 'con', detalle: 'Simulada · modo demostración' }), 700);
      return () => clearTimeout(t);
    }
    if (modo === '3cx') {
      const t = setTimeout(() => dispatch({ tipo: 'ok', paso: 'con', detalle: 'Llamas con la app 3CX' }), 400);
      return () => clearTimeout(t);
    }
    const ws = s.config.ws.trim();
    if (!ws) {
      dispatch({ tipo: 'falla', paso: 'con', detalle: 'Falta la dirección de la pasarela de voz.' });
      return;
    }
    const m = medirCentral(ws);
    let vivo = true;
    m.promesa.then(
      (ms) => vivo && dispatch({ tipo: 'ok', paso: 'con', detalle: calificarLatencia(ms).texto }),
      (e: Error) => vivo && dispatch({ tipo: 'falla', paso: 'con', detalle: e.message }),
    );
    return () => {
      vivo = false;
      m.cancelar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enCurso, st.intento]);

  // Microfono real: abierto mientras se prueba o mientras su fila esta abierta. Se apaga al salir.
  const micActivo = st.pasos.mic === 'run' || (st.abierto === 'mic' && st.pasos.mic === 'ok');
  useEffect(() => {
    if (!micActivo) return;
    let vivo = true;
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    const pide: MediaTrackConstraints | true = micId ? { deviceId: { exact: micId } } : true;
    navigator.mediaDevices
      .getUserMedia({ audio: pide, video: false })
      .then((m) => {
        if (!vivo) return m.getTracks().forEach((t) => t.stop());
        stream = m;
        ctx = new AudioContext();
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        ctx.createMediaStreamSource(m).connect(an);
        analizador.current = an;
        setMicAbierto(true);
        listar();
      })
      .catch((e: { name?: string }) => {
        if (!vivo) return;
        // El microfono guardado ya no esta: volver al predeterminado y reintentar solo.
        if (micId && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) return phone.guardarPreferencias({ microfono: '' });
        dispatch({ tipo: 'falla', paso: 'mic', detalle: mensajeErrorMedios(e) });
      });
    return () => {
      vivo = false;
      analizador.current = null;
      setMicAbierto(false);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
    };
  }, [micActivo, micId, st.intento]);

  // Al pasar todo, se recuerda para no volver a sugerir la prueba.
  useEffect(() => {
    if (listo) phone.guardarPreferencias({ ensayoHecho: Date.now() });
  }, [listo]);

  const tono = useRef<{ el: ConSalida; url: string } | null>(null);
  const [sonando, setSonando] = useState(false);
  const paraTono = () => {
    if (!tono.current) return;
    tono.current.el.pause();
    URL.revokeObjectURL(tono.current.url);
    tono.current = null;
    setSonando(false);
  };
  useEffect(() => paraTono, []);
  const reproducirTono = async () => {
    paraTono();
    const url = URL.createObjectURL(new Blob([wavTono(660, 900)], { type: 'audio/wav' }));
    const el = new Audio(url) as ConSalida;
    el.volume = 0.6;
    tono.current = { el, url };
    try {
      if (altId && el.setSinkId) await el.setSinkId(altId).catch(() => el.setSinkId?.(''));
      setSonando(true);
      el.onended = () => setSonando(false);
      await el.play();
    } catch {
      setSonando(false);
    }
  };

  const fijarMic = (id: string) => phone.guardarPreferencias({ microfono: id });
  const fijarAlt = (id: string) => {
    phone.guardarPreferencias({ altavoz: id });
    if (st.pasos.alt === 'ok' || st.pasos.alt === 'falla') dispatch({ tipo: 'reintenta', paso: 'alt' });
  };
  const micElegido = dispositivoValido(entradas, micId);
  const altElegido = dispositivoValido(salidas, altId);
  const entrada = corto(entradas.find((d) => d.deviceId === (micElegido || 'default')) ?? entradas[0], 'Predeterminado');
  const salida = salidaElegible() ? corto(salidas.find((d) => d.deviceId === (altElegido || 'default')) ?? salidas[0], 'Predeterminada') : 'Del sistema';

  const conectar = () => {
    onConectar();
    onCerrar();
  };
  const titulo = listo ? 'Todo listo' : ocupado ? 'Comprobando tu equipo…' : 'Revisa lo marcado';
  const sub = listo
    ? 'Micrófono y altavoces funcionan. Ya puedes conectarte a la central.'
    : ocupado
      ? 'Revisamos una cosa a la vez. Toca una fila para ver su detalle.'
      : 'Algo necesita tu atención. Puedes conectar igual y probar de nuevo después.';

  return (
    <div className="ens" data-testid="ensayo">
      <div className="ens-top">
        <button className="i-link ens-volver" type="button" onClick={onCerrar}>
          <Icon name="chevLeft" size={16} /> Ajustes
        </button>
        <span className="ens-cap">Antes de llamar</span>
      </div>
      <div className="ens-cab">
        <Anillo hechos={hechos} total={PASOS.length} listo={listo} />
        <div className="ens-tit" aria-live="polite">
          <h3>{titulo}</h3>
          <p>{sub}</p>
        </div>
      </div>
      <ul className="ens-lista">
        {PASOS.map((p) => (
          <FilaPaso key={p.id} id={p.id} titulo={p.titulo} estado={st.pasos[p.id]} abierto={st.abierto === p.id} resumen={st.detalle[p.id] ?? (st.pasos[p.id] === 'ok' ? p.ok : '')} dispatch={dispatch}>
            <p className="ens-ayuda">{st.pasos[p.id] === 'falla' ? st.detalle[p.id] : p.ayuda}</p>
            {p.id === 'mic' ? (
              <>
                {entradas.length > 1 ? (
                  <Selector etiqueta="Entrada" valor={micElegido} lista={entradas} porDefecto="Micrófono predeterminado" onCambio={fijarMic} />
                ) : null}
                {micAbierto ? (
                  <Medidor analizador={analizador} alHablar={() => dispatch({ tipo: 'ok', paso: 'mic' })} esperando={st.pasos.mic === 'run'} />
                ) : st.pasos.mic === 'run' ? (
                  <p className="ens-ayuda">Si el navegador pregunta, pulsa «Permitir».</p>
                ) : null}
                {st.pasos.mic === 'run' && micAbierto ? (
                  <button className="i-link" type="button" onClick={() => dispatch({ tipo: 'ok', paso: 'mic' })}>
                    Sí, la barra se mueve
                  </button>
                ) : null}
              </>
            ) : null}
            {p.id === 'alt' ? (
              <>
                {salidaElegible() && salidas.length > 1 ? (
                  <Selector etiqueta="Salida" valor={altElegido} lista={salidas} porDefecto="Altavoces predeterminados" onCambio={fijarAlt} />
                ) : null}
                <div className="ens-acc">
                  <button className="ens-btn" type="button" onClick={() => void reproducirTono()} aria-pressed={sonando}>
                    <Icon name="play" size={12} /> {sonando ? 'Sonando…' : 'Reproducir tono'}
                  </button>
                  {st.pasos.alt === 'run' ? (
                    <>
                      <span className="ens-pre">¿Lo oyes?</span>
                      <button className="ens-btn prim" type="button" onClick={() => (paraTono(), dispatch({ tipo: 'ok', paso: 'alt' }))}>
                        Sí
                      </button>
                      <button
                        className="ens-btn"
                        type="button"
                        onClick={() => (paraTono(), dispatch({ tipo: 'falla', paso: 'alt', detalle: 'No se oyó. Sube el volumen o elige otra salida y vuelve a probar.' }))}
                      >
                        No
                      </button>
                    </>
                  ) : null}
                </div>
                {!salidaElegible() ? <p className="ens-ayuda">Tu navegador usa la salida de audio del sistema.</p> : null}
              </>
            ) : null}
          </FilaPaso>
        ))}
        <FilaPaso id="cam" titulo="Cámara" estado="opc" abierto={st.abierto === 'cam'} resumen="No se usa" dispatch={dispatch}>
          <p className="ens-ayuda">El teléfono de la Consola solo usa audio. No pedimos permiso de cámara.</p>
        </FilaPaso>
      </ul>
      {listo ? (
        <div className="ens-res">
          {[
            ['Entrada', entrada],
            ['Salida', salida],
            ['Cámara', 'No se usa'],
          ].map(([k, v]) => (
            <div key={k}>
              <span>{k}</span>
              <b title={v}>{v}</b>
            </div>
          ))}
        </div>
      ) : null}
      <div className="btn-col">
        <button className={`i-btnw ${listo ? 'acc' : ''}`} type="button" disabled={ocupado && !listo} onClick={conectar}>
          {listo ? textoConectar : ocupado ? 'Comprobando…' : 'Conectar de todos modos'}
        </button>
        <button className="i-link" type="button" onClick={() => dispatch({ tipo: 'reinicia' })} disabled={ocupado}>
          Volver a comprobar
        </button>
      </div>
    </div>
  );
}

function Anillo({ hechos, total, listo }: { hechos: number; total: number; listo: boolean }) {
  const L = 2 * Math.PI * 52;
  const marcas = Array.from({ length: 40 }, (_, i) => {
    const a = (i / 40) * Math.PI * 2 - Math.PI / 2;
    return { i, x1: 60 + 57 * Math.cos(a), y1: 60 + 57 * Math.sin(a), x2: 60 + 59.5 * Math.cos(a), y2: 60 + 59.5 * Math.sin(a), on: i / 40 < hechos / total };
  });
  return (
    <div className={`ens-anillo ${listo ? 'listo' : ''}`} role="img" aria-label={listo ? 'Todo listo' : `${hechos} de ${total} comprobaciones listas`}>
      <svg viewBox="0 0 120 120" aria-hidden>
        <circle cx="60" cy="60" r="52" className="ens-p" />
        <circle cx="60" cy="60" r="52" className="ens-a" strokeDasharray={L} strokeDashoffset={L * (1 - hechos / total)} transform="rotate(-90 60 60)" />
        {marcas.map((m) => (
          <line key={m.i} x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2} className={`ens-t ${m.on ? 'on' : ''}`} />
        ))}
      </svg>
      {listo ? (
        <span className="ens-ok" aria-hidden>
          <Icon name="check" size={30} />
        </span>
      ) : (
        <span className="ens-n" aria-hidden>
          <b className="mono">{hechos}</b>
          <small>de {total}</small>
        </span>
      )}
    </div>
  );
}

function FilaPaso({
  id,
  titulo,
  estado,
  abierto,
  resumen,
  dispatch,
  children,
}: {
  id: Fila;
  titulo: string;
  estado: EstadoPaso | 'opc';
  abierto: boolean;
  resumen: string;
  dispatch: (a: AccionEnsayo) => void;
  children: ReactNode;
}) {
  const sub = estado === 'run' ? 'Comprobando…' : estado === 'pend' ? 'Sin comprobar' : estado === 'falla' ? 'Necesita atención' : resumen;
  return (
    <li className={`ens-f ${estado} ${abierto ? 'abierto' : ''}`}>
      <button className="ens-fcab" type="button" aria-expanded={abierto} onClick={() => dispatch({ tipo: 'abre', fila: id })}>
        <span className="ens-ico" aria-hidden>
          {estado === 'ok' ? <Icon name="check" size={13} /> : estado === 'falla' ? '!' : estado === 'opc' ? '–' : ''}
        </span>
        <span className="ens-tt">
          <b>
            {titulo}
            {estado === 'opc' ? <small className="opc">Opcional</small> : null}
          </b>
          <small>{sub}</small>
        </span>
        <Icon name="chevDown" size={16} className="i ens-chev" />
      </button>
      {abierto ? (
        <div className="ens-det">
          {children}
          {estado === 'falla' && id !== 'cam' ? (
            <button className="ens-btn" type="button" onClick={() => dispatch({ tipo: 'reintenta', paso: id as PasoId })}>
              <Icon name="refresh" size={13} /> Reintentar
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function Selector({ etiqueta, valor, lista, porDefecto, onCambio }: { etiqueta: string; valor: string; lista: MediaDeviceInfo[]; porDefecto: string; onCambio: (id: string) => void }) {
  const sinDefault = lista.filter((d) => d.deviceId !== 'default');
  return (
    <label className="ens-sel">
      <span>{etiqueta}</span>
      <select className="i-field" value={valor} onChange={(e) => onCambio(e.target.value)}>
        <option value="">{porDefecto}</option>
        {sinDefault.map((d, i) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label || `${etiqueta} ${i + 1}`}
          </option>
        ))}
      </select>
    </label>
  );
}

// Medidor de nivel: lee el analizador en cada cuadro y enciende barras sin re-renderizar React.
function Medidor({ analizador, alHablar, esperando }: { analizador: RefObject<AnalyserNode | null>; alHablar: () => void; esperando: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const aviso = useRef(alHablar);
  aviso.current = alHablar;
  useEffect(() => {
    let id = 0;
    let nivel = 0;
    let desde = 0;
    let avisado = false;
    let datos: Uint8Array<ArrayBuffer> | null = null;
    const lento = reducedMotion();
    let ultimo = 0;
    const cuadro = (t: number) => {
      id = requestAnimationFrame(cuadro);
      if (lento && t - ultimo < 120) return;
      ultimo = t;
      const an = analizador.current;
      const caja = ref.current;
      if (!an || !caja) return;
      if (!datos || datos.length !== an.fftSize) datos = new Uint8Array(an.fftSize);
      an.getByteTimeDomainData(datos);
      nivel = suavizarNivel(nivel, nivelDeMuestras(datos));
      const n = barrasEncendidas(nivel, BARRAS);
      for (let i = 0; i < caja.children.length; i++) caja.children[i].classList.toggle('on', i < n);
      if (esperando && !avisado) {
        if (nivel > UMBRAL_VOZ) {
          if (!desde) desde = t;
          else if (t - desde > 250) {
            avisado = true;
            aviso.current();
          }
        } else desde = 0;
      }
    };
    id = requestAnimationFrame(cuadro);
    return () => cancelAnimationFrame(id);
  }, [analizador, esperando]);
  return (
    <div className="ens-medidor" ref={ref} role="meter" aria-label="Nivel del micrófono" aria-valuemin={0} aria-valuemax={1}>
      {Array.from({ length: BARRAS }, (_, i) => (
        <i key={i} />
      ))}
    </div>
  );
}
